/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Lock, Mail, ArrowLeft } from "lucide-react";

const StaffLoginForm = ({ onSuccessfulLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    setError(''); // Clear error on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/staff-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.type === 'account_locked') {
          throw new Error(data.message || 'Account locked. Please try again later.');
        } else if (data.type === 'account_not_configured') {
          throw new Error(data.message || 'Your account is not configured. Please contact an administrator.');
        } else if (data.type === 'invalid_credentials') {
          throw new Error(data.message || 'Invalid email or password.');
        } else {
          throw new Error(data.error || data.message || 'Login failed');
        }
      }

      // Check if user needs to set/change password
      if (data.mustSetPassword || data.mustChangePassword) {
        // Redirect to password setup page
        navigate('/set-password', {
          state: {
            userId: data.id,
            firstName: data.firstName,
            isNewPassword: data.mustSetPassword,
            message: data.message
          }
        });
        return;
      }

      // Successful login
      console.log('Staff login successful:', data);
      onSuccessfulLogin({
        ...data,
        firstName: data.firstName || data.first_name
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToStudentLogin = () => {
    navigate('/login');
  };

  return (
    <Card className="w-full max-w-md mx-auto m-2 sm:m-4">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-xl sm:text-2xl">Staff Login</CardTitle>
        </div>
        <CardDescription className="text-sm sm:text-base">
          For teachers and administrators only
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Address
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              placeholder="your.email@school.edu"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Password
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              placeholder="Enter your password or PIN"
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading}
              autoComplete="current-password"
            />
            <p className="text-xs text-gray-500">
              First time logging in? Enter your existing PIN as the password.
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>

          <div className="pt-4 border-t">
            <button
              type="button"
              onClick={handleBackToStudentLogin}
              className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-800"
              disabled={isLoading}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Student Login
            </button>
          </div>

          <p className="text-xs text-center text-gray-500 mt-4">
            Students should use the main login page with their Student ID and PIN.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default StaffLoginForm;
